import React from 'react';

import { Text, TextProps } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { common } from '@/styles/styles';

import { useTextOverflowSplit } from './use-text-overflow-split';

type UseSplitTextProps = {
    text: string;
    color: TextProps['color'];
    variant?: TextProps['variant'];
    textDecorationLine?: TextProps['textDecorationLine'];
};

/**
 * Hook that splits overflowing text across two locations.
 *
 * - `Line1` renders inline (inside a flex container on the first row)
 * - `Line2` renders via the returned component (place it below the row)
 *
 * An invisible measuring text always stays in the layout so that
 * width changes (e.g. an icon appearing) trigger a correct re-split.
 */
export const useSplitText = ({ text, color, variant = 'bodyMedium', textDecorationLine }: UseSplitTextProps) => {
    const { textSplit, handleTextLayout } = useTextOverflowSplit(text);

    const Line1 = (
        <Box flex={1}>
            {textSplit ? (
                <Text variant={variant} color={color} textDecorationLine={textDecorationLine} numberOfLines={1}>
                    {textSplit.line1}
                </Text>
            ) : null}
            <Text
                variant={variant}
                color={textSplit ? 'transparent' : color}
                textDecorationLine={textDecorationLine}
                onTextLayout={handleTextLayout}
                style={textSplit ? common.measureLayer : undefined}
            >
                {text}
            </Text>
        </Box>
    );

    const Line2 = textSplit?.line2 ? (
        <Text variant={variant} color={color} textDecorationLine={textDecorationLine}>
            {textSplit.line2}
        </Text>
    ) : null;

    return { Line1, Line2 };
};
